/**
 * For Venues (slot model; mounted at /for-venues when SLOT_MODEL is on — pages/ForVenues.jsx
 * stays for flag-off). Audience: bars and restaurants hosting coasters.
 *
 * Sept 2026 redesign: ports site/for-venues/index.html of the approved package (hero with the
 * VEN-HERO-01 photo and "On us." ticket (U4), image-topped benefit cards, "Good for your tables", the guest
 * story with the example offer, FAQ, "Make room for a little more local" call-out) onto
 * components/site. No coaster figure for venues: quantities and timing are confirmed with
 * each venue (copy audit, investigations/04-copy-audit.md). "Get free coasters" goes to
 * /venue/apply (the redesigned public application, site redesign Phase 5: VenueApplySlot, three
 * steps, no sign-up first); "Talk to the team" opens the enquiry dialog (venue variant) and
 * "Try an example offer" the example-offer dialog (components/site/demo). Labels: the CTA
 * vocabulary of the upgrade brief (§2.2).
 */
import React from 'react';
import SEO from '../../components/SEO';
import { Button, CtaBand, Eyebrow, Heading, PageHero, Pill, SiteImage, SiteSection } from '@/components/site';
import { EnquiryButton } from '@/components/site/enquiry';
import { VENUE_TYPES } from '@/lib/venueTypes';
import {
  AudienceFaq, AudienceMain, BAND_TINT, DemoOfferLink, NumberedCards, PAGE_BUTTON, PaperPanel, PhotoCards, SectionTop, VenueHeroArt,
} from './audienceParts';

/** Upgrade brief §3.4 item 2: image-topped benefit cards (VEN-BEN-01…03). */
const BENEFITS = [
  {
    photo: 'VEN-BEN-01',
    eyebrow: 'Made for your venue',
    title: 'Your name. Your QR.',
    text: 'A local starting point for guests at your tables.',
  },
  {
    photo: 'VEN-BEN-02',
    eyebrow: 'Taken care of',
    title: 'Print. Pack. Deliver.',
    text: 'Tappd handles the coasters, from production to your door.',
  },
  {
    photo: 'VEN-BEN-03',
    eyebrow: 'Part of the routine',
    title: 'Put them out.',
    text: 'Use them as you would any coaster.',
  },
];

const STEPS = [
  {
    title: 'Tell us about your venue.',
    text: 'Share your location, venue type, and approximate guest counts. We’ll review the fit together.',
  },
  {
    title: 'We arrange the coasters.',
    text: 'Tappd coordinates local advertising, your venue details, printing, and delivery.',
  },
  {
    title: 'Bring them to the table.',
    text: 'Keep the coasters in use and share approximate guest counts. Guests can explore the offers without staff explaining an app.',
  },
];

/** The path from applying to guests scanning (upgrade brief §3.4). */
const TIMELINE = ['Apply', 'We confirm the fit', 'Coasters arrive', 'Guests scan'];

const FAQ = [
  {
    q: 'Who pays for the coasters?',
    a: 'Local advertisers pay for placements. Their sponsorship funds the printing and delivery of coasters for participating venues.',
  },
  {
    q: 'How many coasters will we receive?',
    a: 'The team will confirm quantities and cadence for your venue before you join. Delivery depends on your agreed allocation and print schedule.',
  },
  {
    q: 'Does my staff have to sell anything?',
    a: 'No. Staff use the coasters as usual. Guests scan with their phone camera and browse on their own.',
  },
  {
    q: 'How do we get started?',
    a: 'Complete the venue application with your venue and contact details. Tappd will review eligibility, advertising fit, and the next available print timing.',
  },
];

export default function ForVenuesSlot() {
  return (
    <AudienceMain>
      <SEO
        title="For Venues"
        description="Free custom coasters for your bar or restaurant, sponsored by local businesses. Tappd prints and delivers them; guests scan to find local offers."
        url="/for-venues"
        image="/images/og/for-venues.jpg"
      />

      <PageHero
        eyebrow="For bars & restaurants"
        title={(
          <>
            Your tables.
            {' '}
            <br />
            Your neighbors.
            {' '}
            <br />
            <em>Our round.</em>
          </>
        )}
        lede="Free custom coasters with your venue’s name and QR code, paid for by local advertisers. We print and deliver them. Your staff doesn’t have to sell a thing."
        actions={(
          <>
            <Button className={PAGE_BUTTON} to="/venue/apply">Get free coasters</Button>
            <EnquiryButton className={PAGE_BUTTON} role="venue" variant="secondary">Talk to the team</EnquiryButton>
          </>
        )}
        fine="3 quick steps · no sign-up needed to apply"
        media={<VenueHeroArt />}
      />

      <SiteSection aria-label="Why host Tappd coasters" className="pt-10 nav:pt-12">
        <PhotoCards label="Why host Tappd coasters" items={BENEFITS} headingLevel={2} />
      </SiteSection>

      <SiteSection divider aria-labelledby="fv-exchange-title">
        <SectionTop
          eyebrow="A simple exchange"
          titleId="fv-exchange-title"
          title={(
            <>
              Good for your tables.
              {' '}
              <br />
              Good for your town.
            </>
          )}
          intro="Local advertisers fund the coasters. Your venue gives their businesses a place to be discovered."
        />
        <NumberedCards ordered label="How it works for your venue" items={STEPS} />

        {/* Upgrade brief §3.4 items 3–4: the path from applying to guests scanning, then who it's for. */}
        <ol className="mt-[35px] flex list-none flex-col gap-2 p-0 font-label text-xs uppercase tracking-[0.05em] text-navy sm:flex-row sm:flex-wrap sm:items-center sm:gap-3" aria-label="From application to guests scanning" data-testid="venue-timeline">
          {TIMELINE.map((step, i) => (
            <li key={step} className="flex items-center gap-3">
              <span className="border border-navy/30 bg-brand-sheet px-3 py-2">{step}</span>
              {i < TIMELINE.length - 1 ? <span aria-hidden="true" className="hidden text-brand-rust sm:inline">→</span> : null}
            </li>
          ))}
        </ol>
        <p className="mt-3 text-sm text-brand-muted">We’ll confirm eligibility, coaster quantities, and delivery timing.</p>

        <div className="mt-[35px]" data-testid="venue-who">
          <Eyebrow as="h3">Who it’s for</Eyebrow>
          <ul className="mt-3 flex list-none flex-wrap gap-2 p-0" aria-label="Venue types">
            {VENUE_TYPES.filter((t) => t.value !== 'other').map((t) => (
              <li key={t.value}><Pill tone="sheet">{t.label}</Pill></li>
            ))}
          </ul>
          <p className="mt-3 text-[0.9375rem] leading-[1.55]">Starting in Medford, NJ, and expanding across South Jersey.</p>
        </div>
      </SiteSection>

      {/* Upgrade brief §3.4 item 5: VEN-GUEST-01, the guest with the real hub on her phone. */}
      <SiteSection
        divider
        className={BAND_TINT}
        aria-labelledby="fv-guests-title"
        containerClassName="grid grid-cols-1 items-center gap-[35px] nav:grid-cols-2 min-[1051px]:gap-[70px]"
      >
        <figure className="m-0 min-w-0">
          <SiteImage
            id="VEN-GUEST-01"
            ratio="4x5"
            sizes="(min-width: 1808px) 711px, (min-width: 800px) 45vw, 100vw"
            className="border border-brand-rule"
          />
          <figcaption className="mt-3 font-label text-xs uppercase tracking-[0.06em] text-brand-muted">What your guests see</figcaption>
        </figure>
        <PaperPanel className="min-w-0">
          <Eyebrow>What your guests do</Eyebrow>
          <Heading id="fv-guests-title" className="mb-5 mt-5">
            Sip.
            {' '}
            <br />
            Scan.
            {' '}
            <br />
            <em>Find something local.</em>
          </Heading>
          <p className="break-words leading-[1.55] text-brand-muted">No app to download. No account to browse.</p>
          <DemoOfferLink className="mt-5">Try an example offer</DemoOfferLink>
        </PaperPanel>
      </SiteSection>

      {/* Upgrade brief §3.4 item 6: the main-street band (VEN-TOWN-01). */}
      <SiteSection divider aria-labelledby="fv-town-title">
        <div className="max-w-[46rem]">
          <Eyebrow>A little discovery with every round</Eyebrow>
          <Heading id="fv-town-title" className="mb-5 mt-5">
            The next good thing
            {' '}
            <br />
            could be around
            {' '}
            <br />
            the corner.
          </Heading>
          <p className="max-w-[66ch] break-words leading-[1.55] text-brand-muted">
            A gym. A salon. A shop they’ve walked past a hundred times. Your tables become a starting
            point for the businesses that make your neighborhood.
          </p>
        </div>
        <SiteImage
          id="VEN-TOWN-01"
          ratio="16x9"
          navBox="21x9"
          sizes="(min-width: 1808px) 1651px, 100vw"
          className="mt-8 border border-brand-rule"
        />
      </SiteSection>

      <AudienceFaq
        id="fv-faq"
        titleId="fv-faq-title"
        title={(
          <>
            A few good
            {' '}
            <br />
            questions.
          </>
        )}
        items={FAQ}
      />

      <CtaBand
        tone="peach"
        eyebrow="There’s a place for you here"
        title={(
          <>
            Make room for
            {' '}
            <br />
            a little more local.
          </>
        )}
        text="Start with a few details about your venue."
        actions={(
          <>
            <Button className={PAGE_BUTTON} to="/venue/apply">Get free coasters</Button>
            <EnquiryButton className={PAGE_BUTTON} role="venue">Talk to the team</EnquiryButton>
          </>
        )}
      />
    </AudienceMain>
  );
}
